<template>
  <div class="employer-profile-form">
    <BasePreloader v-if="pending"/>
    <div class="d-flex">
      <h1 class="employer-profile-form__title">{{ $tEmployerProfileForm('title') }}</h1>

      <div class="employer-profile-form__verification-alert-wrap">
        <div class="employer-profile-form__verification-alert">
          <div
            class="employer-profile-form__verification-chip"
            :style="{ backgroundColor: verificationChip.color }"
          >
            <BaseSvg :name="verificationChip.icon.name" width="1.2rem" height="1.2rem" />
            {{ profileStatus.label }}
          </div>
          <div
            v-if="['on_moderation', 'not_save'].includes(profileStatus.status)"
            class="employer-profile-form__verification-alert-tooltip-wrap"
            :class="{ _open: show.verificationAlertTooltip }"
            @mouseleave="show.verificationAlertTooltip = false"
          >
            <div class="employer-profile-form__verification-alert-info">?</div>
            <!--      TODO temporary tooltip class      -->
            <div
              class="vacancy-one-response__item-tooltip"
              style="z-index: 90"
            >
              {{ profileStatus.text }}
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="employer-profile-form__row">
      <div class="employer-profile-form__custom-input  _email">
        <div class="employer-profile-form__custom-input-label">
          <span>E-mail</span>
        </div>
        <div class="employer-profile-form__custom-input-wrap"
             style="position:relative;">
          <BasePreloader v-if="pendingConfirmEmail"/>
          <div v-if="inputs.email.confirmed === true"
               class="employer-profile-form__custom-input-item  _done">
            <BaseSvg name="ico-check"/>
            <span>{{ $tEmployerProfileForm('status_confirmed') }}</span>
          </div>
          <div v-else-if="inputs.email.confirmed === false"
               class="employer-profile-form__custom-input-item  _rejected">
            <BaseSvg name="ico-close"/>
            <span>{{ $tEmployerProfileForm('status_not_confirmed') }}</span>
          </div>
          <BaseInput v-model="inputs.email.value"
                     :error="inputs.email.error"
                     readonly
                     type="email"
                     class="employer-profile-form__custom-input"/>
        </div>
      </div>
      <div v-if="showEmailConfirmBtn" class="employer-profile-form__custom-input  _confirm-email">
        <div class="employer-profile-form__custom-input-label">
          <span style="visibility: hidden">.</span>
        </div>
        <div>
          <button
            type="button"
            :disabled="!inputs.email.value || !inputs.email.value.includes('@')"
            class="base-btn base-btn--outline-dark"
            @click.prevent="pendingSendEmailConfirmation ? null : sendEmailConfirmation()"
          >
            <span>{{ $tEmployerProfileForm('confirm_email_btn') }}</span>
            <BasePreloader v-if="pendingSendEmailConfirmation" class="_small"/>
          </button>
        </div>
      </div>
    </div>
    <div class="employer-profile-form__row">
      <BaseInput v-model="inputs.name.value"
                 :error="inputs.name.error"
                 :disabled="!canModerate"
                 :label="$tEmployerProfileForm('company_name')"
                 :tooltip-text="$tEmployerProfileForm('company_name_tooltip')"
                 class=""/>
      <BaseSelect
        v-model="inputs.companyType.value"
        :label="$tEmployerProfileForm('company_type')"
        :options="companyTypes"
        text-key="title"
        :disabled="!canModerate"
        :error="inputs.companyType.error"
      />
    </div>
    <div class="employer-profile-form__row">
      <BaseInput
        v-model="inputs.brand_name.value"
        :label="$tEmployerProfileForm('brand_name')"
        :tooltip-text="$tEmployerProfileForm('brand_name_tooltip')"
        :error="inputs.name.error"
        :disabled="!canModerate"
      />
      <BaseSelect
        v-model="inputs.ownership.value"
        :label="$tEmployerProfileForm('ownership')"
        :options="ownershipTypes"
        text-key="title"
        :disabled="!canModerate"
        :error="inputs.ownership.error"
      />
    </div>
    <div class="employer-profile-form__row">
      <SCountry v-model="inputs.country.value"
                :error="inputs.country.error"
                :disabled="!canModerate"
                class="_country"
                :label="$tEmployerProfileForm('country')"/>
      <div class="employer-profile-form__custom-input _code">
        <div class="employer-profile-form__custom-input-label">
          <span>{{ $tEmployerProfileForm('company_code') }}</span>
          <BaseTooltip
            :text="$tEmployerProfileForm('company_code_tooltip')"
            class="d-inline-flex"
            style="height: 1.4rem;"
            tooltip-menu-style="max-width: 24rem"
          >
            <template v-slot:activator>
              <div class="input__tooltip-activator">?</div>
            </template>
          </BaseTooltip>
        </div>
        <div class="employer-profile-form__custom-input-wrap">
          <div v-if="codeStatus"
               :class="'_' + codeStatus.status"
               class="employer-profile-form__custom-input-item">
            <BaseSvg v-if="codeStatus.status === 'done'"
                     name="ico-check"/>
            <BaseSvg v-if="codeStatus.close === 'rejected'"
                     name="ico-check"/>
            <BaseSvg v-if="codeStatus.status === 'pending'"
                     width="1.1rem"
                     height="1.2rem"
                     name="ico-clock"/>
            <span>{{codeStatus.title}}</span>
            <BaseHelp v-if="inputs.code.comment"
                      :value="inputs.code.comment"
                      width="20rem"
                      class="_top"/>
          </div>
          <BaseInput v-model="inputs.code.value"
                     type="number"
                     :disabled="!canModerate"
                     :error="inputs.code.error"
                     @input="inputs.code.confirmed = null"
                     class="employer-profile-form__custom-input"/>
        </div>
      </div>
      <!--   Confirm code btn   -->
      <div v-if="false" class="employer-profile-form__custom-input _check-code">
        <div class="employer-profile-form__custom-input-label">
          <span style="visibility: hidden">.</span>
        </div>
        <div>
          <button
            type="button"
            :disabled="disabledCodeCheckBtn"
            class="base-btn base-btn--outline-dark"
            @click.prevent="pendingCheckCode ? null : checkCode()"
          >
            <span>{{ $tEmployerProfileForm('check_code_btn') }}</span>
          </button>
        </div>
      </div>
    </div>
    <div class="employer-profile-form__row">
      <BaseInputPhone
        v-model="inputs.phone.value"
        :label="$tEmployerProfileForm('Phone number')"
        :disabled="!canModerate"
        :error="inputs.phone.error"
        class="employer-profile-form__custom-input"
        @input="onPhoneInput"
        @validate="onPhoneValidate"
      />
      <BaseInput
        v-model="inputs.phone_name.value"
        :label="$tEmployerProfileForm('Name')"
        :disabled="!canModerate"
        :error="inputs.phone_name.error"
        class="employer-profile-form__custom-input"
      />
    </div>
    <div class="employer-profile-form__row relative">
      <div
        v-if="receivedData && !receivedData.email.confirmed"
        class="card-disable-cover _white"
        style="opacity: 0.5; z-index: 10;"
      ></div>
      <div class="employer-profile-form__custom-input">
        <div class="employer-profile-form__custom-input-label">
          <span>{{ $tEmployerProfileForm('logo') }}</span>
        </div>
        <FilesUploadMultiple v-model="logo"
                             :max="1"
                             :filesExtsMixin="filesExtsMixin"
                             :filesMimeTypes="filesMimeTypes"
                             :pendingFile="pendingDeleteLogo"
                             outsideDelete
                             @input="onImageLoad"
                             @delete="deleteLogo"/>
        <div class="employer-profile-form__custom-input-descr">
          {{ $tEmployerProfileForm('logo_note') }}
        </div>
      </div>
    </div>

    <div class="employer-profile-form__row mt-5">
      <div>
        <div class="employer-profile-form__custom-input-label">
          <span>{{ $tEmployerProfileForm('registration_documents') }}</span>
        </div>
        <UploadFilesList
          v-model="registrationDocuments"
          :loading="loadingRegistrationFiles"
          load-without-id
          :load-files-idx="loadingRegistrationFilesIds"
          @upload="onRegistrationFilesUpload"
          @delete="onRegistrationFileDelete"
        />
      </div>
    </div>

    <BaseAlert :value="overallErrorsMix"
               class="employer-profile-form__error  _warning"/>

    <div class="employer-profile-form__footer">
      <button
        :disabled="sendButtonAvailable"
        class="base-btn base-btn--blue"
        @click.prevent="send()"
      >
        <span>{{ $tEmployerProfileForm('save_btn') }}</span>
        <BasePreloader v-if="pendingSend" class="_small"/>
      </button>
      <button class="base-btn base-btn--outline-dark" @click="changePassword">
        <BaseSvg name="ico-key" width="1.2rem" height="1.2rem"/>
        <span>{{ $tEmployerProfileForm('change_password_btn') }}</span>
      </button>
    </div>
  </div>
</template>

<script>
  import {http} from "@/axios"
  import { serialize } from 'object-to-formdata';
  import formMixins from "@/mixins/formMixins"
  import getFileExtension from '@/helpers/getFileExtension'
  import SCountry from '@/components/loadingSelects/SCountry'
  import FilesUploadMultiple from '@/components/FilesUploadMultiple'
  import UploadFilesList from './files/UploadFilesList';
  import {
    ALLOWED_IMAGES_EXTS,
    IMAGES_MIME_TYPES
  } from '@/constants/files'
  import {mapActions} from 'vuex';

  export default {
    name: 'AccountProfileForm',
    mixins: [formMixins],
    components: {
      SCountry,
      FilesUploadMultiple,
      UploadFilesList,
    },
    data() {
      return {
        show: {
          verificationAlertTooltip: false,
        },
        sentEmailConfirm: false,

        registrationDocuments: [],
        loadingRegistrationFiles: false,
        loadingRegistrationFilesIds: [],

        filesExtsMixin: [...ALLOWED_IMAGES_EXTS],
        filesMimeTypes: [...IMAGES_MIME_TYPES],

        pending: false,
        pendingSend: false,
        pendingCheckCode: false,
        pendingDeleteLogo: false,
        pendingSendEmailConfirmation: false,
        pendingConfirmEmail: false,

        receivedData: null,

        logo: [],
        inputs: {
          email: {
            value: null,
            confirmed: null,
            required: true
          },
          name: {
            value: null,
            required: true
          },
          companyType: {
            value: null,
            required: true,
          },
          brand_name: {
            value: null,
            required: true,
          },
          ownership: {
            value: null,
            required: true,
          },
          code: {
            value: null,
            confirmed: null,
            message: null,
            required: true
          },
          country: {
            value: null,
            required: true
          },
          phone: {
            value: null,
            required: false,
            error: null,
          },
          phone_name: {
            value: null,
            required: true
          },
        },
        phoneValidationData: null,

        profileStatus: {},
      };
    },
    computed: {
      codeStatus() {
        // 0 - Ничего (код не передан)
        // 1 - Код передан и проверен (успешно)
        // 2 - Код передан и проверен (не успешно)
        // 3 - Код передан и находится на проверке (желтый статус - На проверке)
        switch (this.inputs.code.confirmed) {
          case (1):
            return {
              status: 'done',
              title: this.$tEmployerProfileForm('status_confirmed'),
            }
          case (2):
            return {
              status: 'rejected',
              title: this.$tEmployerProfileForm('status_not_confirmed'),
            }
          case (3):
            return {
              status: 'pending',
              title: this.$tEmployerProfileForm('status_pending'),
            }
        }

        return null
      },
      disabledCodeCheckBtn() {
        const codeVal = this.inputs.code.value;
        return !codeVal || codeVal.length < 5 || codeVal.length > 15 || !/^\d+$/.test(codeVal)
          || (this.codeStatus && this.codeStatus.status !== 'rejected');
      },
      canModerate() {
        return !['success', 'on_moderation'].includes(this.profileStatus.status) && this.receivedData?.email.confirmed;
      },
      verificationChip() {
        switch (this.profileStatus.status) {
          case 'success':
            return { color: '#19c25d', icon: { name: 'ico-check' }, };
          case 'not_save':
            return { color: '#adadad', icon: { name: 'ico-cross' } };
          case 'on_moderation':
            return { color: '#f0ae2e', icon: { name: 'ico-cog' } };
          case 'rejected':
            return { color: '#EB4948', icon: { name: 'ico-cross' } };
          default:
            return { color: '#adadad', icon: { name: 'ico-cross' } };
        }
      },
      showEmailConfirmBtn() {
        return !this.receivedData?.email.confirmed && !this.sentEmailConfirm;
      },
      companyTypes() {
        return [
          {
            id: 1,
            title: this.$tEmployerProfileForm('Recruiter'),
          },
          {
            id: 2,
            title: this.$tEmployerProfileForm('Direct employer'),
          },
          {
            id: 3,
            title: this.$tEmployerProfileForm('Leasing company'),
          },
        ];
      },
      ownershipTypes() {
        return [
          {
            id: 1,
            title: this.$tEmployerProfileForm('individual_entrepreneur'),
          },
          {
            id: 2,
            title: this.$tEmployerProfileForm('legal_entity'),
          },
        ];
      },
      sendButtonAvailable() {
        return this.pendingSend || !this.canModerate || this.inputs.phone.error;
      },
    },
    watch: {
      'inputs.code.value'(value, prevValue) {
        if (value?.length > 15) this.inputs.code.value = prevValue;
      },
    },
    methods: {
      ...mapActions('user', ['updateEmployerProfile']),

      showSavedDataModal() {
        this.$vModal.open('successfully-sent', {
          title: this.$tEmployerProfileForm('successfully-sent-title'),
        });
      },
      load() {
        this.pending = true

        return http.get('/api/cabinet/company-profile-v2/get')
            .then(({data}) => {
              this.parseReceivedData(data.data)
            })
            .catch(this.errorHandlerMix)
            .finally(() => {
              this.pending = false
            })
      },
      async onImageLoad() {
        if (!this.canModerate && this.receivedData.email.confirmed) {
          const success = await this.send({ showSuccessModal: false });
          if (success) this.showSavedDataModal();
        }
      },
      onPhoneInput(value) {
        if (this.phoneValidationData) {
          const phoneStart = '+' + this.phoneValidationData.country.dialCode;

          if (value.length < phoneStart.length)
            this.inputs.phone.error = null;
          else
            this.onPhoneValidate();
        }
      },
      onPhoneValidate(data = this.phoneValidationData) {
        this.phoneValidationData = data;
        const { isValid, number, country } = data;

        const invalidLength = (number.significant || !number.hasOwnProperty('significant')) && this.inputs.phone.value?.length > (country.dialCode.length + 1);
        if (invalidLength && !isValid) {
          this.inputs.phone.error = this.$tEmployerProfileForm('phone_field_invalid');
        } else this.inputs.phone.error = null;
      },
      send(params = { showSuccessModal: true }) {
        this.resetErrorsMix(this.inputs)

        const sendData = this.createSendDataMix(this.inputs)
        if (this.isRequiredEmptyMix) return

        const formData = new FormData()

        for (const key in sendData) {
          let value = sendData[key] || '';

          if (key === 'ownership' && !value) value = 0;

          formData.append(key, value)
        }

        if (this.logo.length && this.logo[0].file) {
          formData.append('logo', this.logo[0].file)
        }

        this.pendingSend = true

        return http.post('/api/cabinet/company-profile-v2/save', formData)
            .then(({data}) => {
              this.parseReceivedData(data.data);
              this.updateEmployerProfile({ profile_status: this.profileStatus });

              if (params.showSuccessModal) {
                this.$vModal.open('successfully-sent', {
                  title: this.$tEmployerProfileForm('Profile submitted for moderation'),
                })
              }
              return true;
            })
            .catch(this.errorHandlerMix)
            .finally(() => {
              this.pendingSend = false
            })
      },
      parseReceivedData(data) {
        this.receivedData = data
        this.profileStatus = data.profile_status;

        const inputs = this.inputs

        inputs.email.value = data.email.email
        inputs.email.confirmed = data.email.confirmed
        inputs.name.value = data.name
        inputs.companyType.value = this.getCompanyTypeWithId(data.company_type)
        inputs.brand_name.value = data.brand_name
        inputs.ownership.value = this.getOwnershipTypeWithId(data.ownership)
        inputs.country.value = data.country
        inputs.phone.value = data.phone
        inputs.phone_name.value = data.phone_name
        inputs.code.value = data.code.code
        inputs.code.confirmed = data.code.confirmed
        inputs.code.comment = data.code.comment

        this.logo = data.logo ? [{path: data.logo, ext: getFileExtension(data.logo)}] : [];

        if (this.profileStatus.status === 'on_moderation') {
          this.show.verificationAlertTooltip = true;
        }
      },
      getCompanyTypeWithId(id) {
        return this.companyTypes.find(t => t.id === (id || 2))
      },
      getOwnershipTypeWithId(id) {
        return this.ownershipTypes.find(t => t.id === id);
      },
      checkCode() {
        this.pendingCheckCode = true

        this.inputs.code.message = null

        const sendData = {
          code: this.inputs.code.value
        }

        http.post('/api/cabinet/company-profile-v2/check-code', sendData)
            .then(({data: {data}}) => {
              if (data.code.confirmed) {
                this.inputs.code.confirmed = data.code.confirmed
                this.inputs.code.comment = data.code.comment
              } else {
                this.inputs.code.confirmed = 0
                this.inputs.code.comment = data.code.comment
              }
            })
            .catch(this.errorHandlerMix)
            .finally(() => {
              this.pendingCheckCode = false
            })
      },
      deleteLogo() {
        this.$vModal.open('confirm', {
          title: this.$tEmployerProfileForm('confirm-delete-logo-title'),
          onAccept: () => {
            this.pendingDeleteLogo = true

            http.delete('/api/cabinet/company-profile-v2/logo')
                .then(() => {
                  this.logo = []
                  this.showSavedDataModal();
                })
                .catch(this.errorHandlerMix)
                .finally(() => {
                  this.pendingDeleteLogo = false
                })
          }
        })
      },
      sendEmailConfirmation() {
        this.pendingSendEmailConfirmation = true

        http.post('/api/cabinet/company-profile-v2/send-confirm-email')
            .then(({data}) => {
              this.sentEmailConfirm = true;

              this.$vModal.open('successfully-sent', {
                title: data.message,
              })
            })
            .catch(this.errorHandlerMix)
            .finally(() => {
              this.pendingSendEmailConfirmation = false
            })
      },
      confirmEmail() {
        const token = this.$route.query.confirm_token
        if (!token) return

        this.pendingSendEmailConfirmation = true

        return http.post('/api/cabinet/company-profile-v2/confirm-email', {
          confirm_token: token
        })
            .then(() => {
              this.inputs.email.confirmed = true
            })
            .finally(() => {
              this.pendingSendEmailConfirmation = false
              this.load();
            })
      },
      changePassword() {
        this.$vModal.open('change-password-confirm', {
          title: this.$tEmployerProfileForm('change-password-title'),
          message: this.$tEmployerProfileForm('change-password-message'),
          btn: this.$tEmployerProfileForm('change-password-btn'),
          onAccept: () => {
            this.$vModal.close('change-confirm')
          }
        })
      },
      async loadRegistrationDocuments() {
        this.loadingRegistrationFiles = true;
        try {
          const { data } = await http.get('/api/cabinet/company-profile-v2/get-documents');
          this.registrationDocuments = data.data;
        } finally {
          this.loadingRegistrationFiles = false;
        }
      },
      onRegistrationFilesUpload(files, onLoadEnd) {
        const data = serialize({ documents: files });

        http
          .post('/api/cabinet/company-profile-v2/upload-documents', data)
          .then(({ data }) => {
            this.registrationDocuments = data.data;
            onLoadEnd();
          });
      },
      async onRegistrationFileDelete(file) {
        this.loadingRegistrationFilesIds.push(file.id);
        try {
          await http.delete('/api/cabinet/company-profile-v2/delete-document', { data: { document_id: file.id } });
          this.registrationDocuments = this.registrationDocuments.filter(f => f.id !== file.id);
        } finally {
          const idx = this.loadingRegistrationFilesIds.findIndex(id => id === file.id);
          this.loadingRegistrationFilesIds.splice(idx, 1);
        }
      },
    },
    async created() {
      this.loadRegistrationDocuments();
      await this.load()
      await this.confirmEmail()
    }
  }
</script>

<style>
</style>
